/**
 * React Query hook for fetching time entries.
 */

import { useQuery } from '@tanstack/react-query';
import { api, type TimeEntry, type ApiRequestError } from '../lib/api';

export interface UseTimeEntriesOptions {
  taskId?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

export interface UseTimeEntriesResult {
  entries: TimeEntry[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  error: ApiRequestError | null;
  refetch: () => void;
}

/**
 * Fetch time entries for the current user.
 */
export function useTimeEntries(
  options: UseTimeEntriesOptions = {}
): UseTimeEntriesResult {
  const { taskId, limit = 50, offset = 0, enabled = true } = options;

  const query = useQuery({
    queryKey: ['timeEntries', { taskId, limit, offset }],
    queryFn: () => api.getMyTimeEntries({ taskId, limit, offset }),
    enabled,
    staleTime: 30_000, // 30 seconds
    retry: (failureCount, error) => {
      if (error instanceof Error && 'status' in error) {
        const status = (error as ApiRequestError).status;
        if (status === 401 || status === 403) return false;
      }
      return failureCount < 3;
    },
  });

  return {
    entries: query.data?.data ?? [],
    total: query.data?.pagination?.total ?? 0,
    hasMore: query.data?.pagination?.hasMore ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as ApiRequestError | null,
    refetch: query.refetch,
  };
}
